import { useCallback, useState } from 'react';

import { Scheduler } from '@aldabil/react-scheduler';
import { ProcessedEvent, ViewEvent } from '@aldabil/react-scheduler/types';
import { Block } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';

import { openDialog } from '@/components/Dialog';
import {
  AGENDA_BLOCK_EVENT_PREFIX,
  convertAgendaBlockToProcessedEvent,
  convertMaintenanceToProcessedEvent,
} from '@/helpers/maintenances';
import AppError from '@/interfaces/AppError';
import { IMaintenance, IMaintenanceStatus } from '@/interfaces/maintenance/IMaintenance';
import maintenancesService from '@/services/maintenances';

import AgendaBlockForm from '../AgendaBlockForm';
import MaintenanceForm from '../Form';
import MaintenanceSchedulerDetails from '../MaintenanceSchedulerDetails';

export default function MaintenanceScheduler() {
  const { enqueueSnackbar } = useSnackbar();

  // 2.6.2 exposes no imperative API, so remounting via key is the only
  // reliable way to pull a newly created block onto the calendar.
  const [reloadKey, setReloadKey] = useState(0);

  const getRemoteEvents = useCallback(
    async (query: ViewEvent): Promise<ProcessedEvent[]> => {
      const [maintenanceListResponse, agendaBlocks] = await Promise.all([
        maintenancesService.listMaintenances({
          startDate: query.start,
          endDate: query.end,
        }),
        maintenancesService.listAgendaBlocks({
          startDate: query.start,
          endDate: query.end,
        }),
      ]);

      return [
        ...maintenanceListResponse.map(maintenance =>
          convertMaintenanceToProcessedEvent(maintenance)
        ),
        ...agendaBlocks.map(block => convertAgendaBlockToProcessedEvent(block)),
      ];
    },
    []
  );

  // Only agenda blocks are deletable; maintenances are cancelled from their
  // detail page and are flagged deletable: false.
  const handleDelete = useCallback(
    async (deletedId: string | number) => {
      const id = String(deletedId);

      if (!id.startsWith(AGENDA_BLOCK_EVENT_PREFIX)) {
        return undefined;
      }

      try {
        await maintenancesService.deleteAgendaBlock(
          id.slice(AGENDA_BLOCK_EVENT_PREFIX.length)
        );
        enqueueSnackbar('Agenda block removed', { variant: 'success' });
        return deletedId;
      } catch (error) {
        enqueueSnackbar(
          error instanceof AppError
            ? error?.message
            : 'Error removing the agenda block. Please, try again later',
          { variant: 'error' }
        );
        return undefined;
      }
    },
    [enqueueSnackbar]
  );

  const openBlockDialog = useCallback(() => {
    openDialog({
      title: 'Block agenda',
      content: (
        <AgendaBlockForm
          onCreated={() => setReloadKey(current => current + 1)}
        />
      ),
    });
  }, []);

  const updateMaintenanceOnDrop = useCallback(
    async (
      droppedOn: Date,
      updatedEvent: ProcessedEvent,
      originalEvent: ProcessedEvent
    ) => {
      const maintenance = originalEvent?.maintenance as
        | IMaintenance
        | undefined;

      if (!maintenance || maintenance.status !== IMaintenanceStatus.PENDING) {
        return undefined;
      }

      try {
        const appointmentDurationInSeconds =
          (new Date(maintenance.expectedEndDate).getTime() -
            new Date(maintenance.expectedStartDate).getTime()) /
          1000;

        const updatedMaintenance = await maintenancesService.editMaintenance({
          maintenanceId: maintenance.id,
          maintenanceTypeId: maintenance.type.id,
          startDate: droppedOn,
          vehicleId: maintenance.vehicle.vin,
          duration: appointmentDurationInSeconds,
        });

        return {
          ...updatedEvent,
          maintenance: updatedMaintenance,
        };
      } catch (error) {
        enqueueSnackbar(
          error instanceof AppError
            ? error?.message
            : `Error updating maintenance. Please, try again later`,
          { variant: 'error' }
        );
        return undefined;
      }
    },
    [enqueueSnackbar]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Block />}
          onClick={openBlockDialog}
        >
          Block agenda
        </Button>
      </Box>

      <Scheduler
        key={reloadKey}
        view="week"
      customEditor={scheduler => <MaintenanceForm scheduler={scheduler} />}
      day={{
        startHour: 7,
        endHour: 20,
        step: 60,
      }}
      onEventDrop={updateMaintenanceOnDrop}
      month={{
        weekDays: [0, 1, 2, 3, 4, 5],
        weekStartOn: 1,
        startHour: 7,
        endHour: 20,
        navigation: true,
      }}
      week={{
        weekDays: [0, 1, 2, 3, 4, 5],
        weekStartOn: 1,
        startHour: 7,
        endHour: 20,
        step: 60,
        navigation: true,
      }}
      viewerExtraComponent={(_, event) => {
          const maintenance = event.maintenance as IMaintenance | undefined;

          // Agenda blocks share the viewer but carry no maintenance.
          if (!maintenance) {
            return <div />;
          }

          return (
            <MaintenanceSchedulerDetails
              maintenanceId={maintenance.id}
              description={maintenance.description}
              vehicleVin={maintenance.vehicle.vin}
            />
          );
        }}
        onDelete={handleDelete}
        getRemoteEvents={getRemoteEvents}
      />
    </Box>
  );
}
